/**
 * @ipc/admin-ui · shared/api/api-client — единый фасад доступа к backend.
 *
 * ЕДИНСТВЕННАЯ точка fetch в приложении: компоненты НЕ вызывают fetch напрямую,
 * только через `apiClient.request` (+ useQuery/useMutation). Разбирает РЕАЛЬНУЮ
 * wire-оболочку backend (ResponseFormatter, раздел 13):
 *   успех:  { success:true,  data:T,   errors:null,       meta:object|null }
 *   ошибка: { success:false, data:null, errors:string[],  meta:{code,fields?,details?} }
 * webhooks-контроллер отдаёт УКОРОЧЕННУЮ форму { success, data } без errors/meta —
 * разбор это допускает (errors/meta необязательны на успехе).
 *
 * Транспорт SPA: Authorization: Bearer <accessToken> всегда; X-Tenant-Id на admin-маршрутах;
 * Idempotency-Key (randomUUID) при idempotent:true.
 *
 * Сессия:
 * - access JWT ~15 мин; refresh TTL 30 дней sliding (rotation на каждом refresh);
 * - POST /admin/auth/refresh → { accessToken, refreshToken }; оба пишем в token-store;
 * - при 401 (не auth-роуты) — single-flight POST /admin/auth/refresh, затем retry 1 раз;
 * - proactive refresh, если exp access < 60 с до запроса;
 * - clearTokens + /login только при окончательном провале refresh (нет/невалиден refresh),
 *   не при временных сетевых сбоях.
 */
import { API_BASE_URL } from '@/shared/config/env';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './token-store';
import { getTenantId } from './tenant-store';
import { isPlainObject, str } from './guards';

/** Порог proactive refresh: обновляем access, если до exp осталось меньше этого (сек). */
const PROACTIVE_REFRESH_SKEW_SEC = 60;

/** Ошибка запроса. status — HTTP-код; code — бизнес-код из meta.code; messages — errors[]. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messages: string[];

  constructor(status: number, code: string, messages: string[]) {
    super(messages[0] ?? code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.messages = messages;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  searchParams?: Record<string, string | number | boolean | undefined | null>;
  /** Добавить заголовок Idempotency-Key: <uuid>. Обязателен для POST создающих/refund-роутов. */
  idempotent?: boolean;
  signal?: AbortSignal;
}

/** Метаданные ответа (meta): пагинация page-based + произвольные ключи. */
export interface ResponseMeta {
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  nextCursor?: number | null;
  [key: string]: unknown;
}

/** Результат request: сырое data (unknown — сузит маппер вызывающего) + meta + заголовки. */
export interface ApiResponse {
  data: unknown;
  meta: ResponseMeta | null;
  headers: Headers;
}

function buildUrl(path: string, searchParams: RequestOptions['searchParams']): string {
  const base = API_BASE_URL === '' ? path : `${API_BASE_URL}${path}`;
  if (!searchParams) {
    return base;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `${base}?${qs}` : base;
}

/** true для путей, требующих заголовок X-Tenant-Id (admin-маршруты). */
function needsTenantHeader(path: string): boolean {
  return path.startsWith('/admin');
}

function authHeaders(path: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token !== null && token.length > 0) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (needsTenantHeader(path)) {
    headers['X-Tenant-Id'] = getTenantId();
  }
  return headers;
}

/** 401 окончательный → сбросить токены и уйти на /login (кроме самой /login). */
function handleUnauthorized(): void {
  clearTokens();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

/**
 * Роуты аутентификации: их 401 — осмысленный бизнес-ответ, не «протухшая сессия».
 * Нельзя перехватывать глобально (refresh/retry/сброс): наверх уходит реальный текст бэка.
 * covers: /telegram, /telegram-twa (prefix), /refresh, /logout.
 */
function isAuthRoute(path: string): boolean {
  return (
    path.startsWith('/admin/auth/telegram') ||
    path.startsWith('/admin/auth/refresh') ||
    path.startsWith('/admin/auth/logout')
  );
}

/**
 * Base64URL → строка (без verify). Паддинг восстанавливаем для atob.
 */
function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (b64.length % 4)) % 4;
  const padded = b64 + '='.repeat(padLen);
  return atob(padded);
}

/**
 * Читает exp из access JWT без проверки подписи — только для proactive TTL-check.
 * Подпись и валидность проверяет backend.
 */
function getJwtExpSeconds(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || parts[1] === undefined || parts[1].length === 0) {
      return null;
    }
    const json = base64UrlDecode(parts[1]);
    const payload: unknown = JSON.parse(json);
    if (!isPlainObject(payload)) {
      return null;
    }
    const exp = payload['exp'];
    return typeof exp === 'number' && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/** true, если до exp access осталось меньше skew секунд (или уже истёк). */
function accessTokenNeedsRefresh(token: string, skewSeconds = PROACTIVE_REFRESH_SKEW_SEC): boolean {
  const exp = getJwtExpSeconds(token);
  if (exp === null) {
    return false;
  }
  const nowSec = Math.floor(Date.now() / 1000);
  return exp - nowSec < skewSeconds;
}

/**
 * Single-flight mutex на refresh: concurrent 401 ждут один POST /admin/auth/refresh,
 * а не N параллельных refresh.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Обмен refresh → новая JWT-пара (rotation). true = токены обновлены; false = не удалось.
 * Backend: POST /admin/auth/refresh → { accessToken, refreshToken }; refresh TTL 30 дней sliding.
 * - нет/невалиден refresh (401/403) → clearTokens + redirect, false
 * - сеть / 5xx / не-JSON → токены НЕ чистим, false (временный сбой)
 */
async function tryRefreshAccessToken(): Promise<boolean> {
  if (refreshInFlight !== null) {
    return refreshInFlight;
  }
  refreshInFlight = doRefreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null || refreshToken.length === 0) {
    handleUnauthorized();
    return false;
  }

  const url = buildUrl('/admin/auth/refresh', undefined);
  let response: Response;
  try {
    // Сырой fetch: не через request(), чтобы не зациклить interceptor и не тащить session-api.
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders('/admin/auth/refresh'),
      },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Сеть недоступна — refresh и access оставляем, повтор возможен позже.
    return false;
  }

  // Окончательный отказ auth: refresh протух / отозван.
  if (response.status === 401 || response.status === 403) {
    handleUnauthorized();
    return false;
  }

  // Временный сбой backend — не разлогиниваем.
  if (!response.ok) {
    return false;
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return false;
  }

  if (!isPlainObject(parsed) || parsed['success'] !== true) {
    // 200, но success:false — нестандарт; токены не трогаем (нет явного 401).
    return false;
  }

  const data = parsed['data'];
  if (!isPlainObject(data)) {
    return false;
  }

  const accessToken = str(data['accessToken']);
  if (accessToken.length === 0) {
    return false;
  }

  // Rotation: backend отдаёт новую пару; оба токена обязательны для sliding refresh (TTL 30 дней).
  const nextRefresh = str(data['refreshToken']);
  if (nextRefresh.length > 0) {
    setTokens(accessToken, nextRefresh);
  } else {
    // Fallback без rotation — access обновляем, старый refresh оставляем.
    setTokens(accessToken);
  }
  return true;
}

/**
 * Перед запросом: если access вот-вот истечёт и есть refresh — обновить заранее.
 * Провал proactive (сеть) не блокирует исходный запрос.
 */
async function maybeProactiveRefresh(path: string): Promise<void> {
  if (isAuthRoute(path)) {
    return;
  }
  const access = getAccessToken();
  if (access === null || access.length === 0) {
    return;
  }
  if (!accessTokenNeedsRefresh(access)) {
    return;
  }
  const refresh = getRefreshToken();
  if (refresh === null || refresh.length === 0) {
    return;
  }
  await tryRefreshAccessToken();
}

function parseMeta(raw: unknown): ResponseMeta | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const meta: ResponseMeta = {};
  const pagination = raw['pagination'];
  if (isPlainObject(pagination)) {
    meta.pagination = {
      page: typeof pagination['page'] === 'number' ? pagination['page'] : 0,
      limit: typeof pagination['limit'] === 'number' ? pagination['limit'] : 0,
      total: typeof pagination['total'] === 'number' ? pagination['total'] : 0,
      totalPages: typeof pagination['totalPages'] === 'number' ? pagination['totalPages'] : 0,
    };
  }
  if (typeof raw['nextCursor'] === 'number' || raw['nextCursor'] === null) {
    meta.nextCursor = raw['nextCursor'];
  }
  for (const [key, value] of Object.entries(raw)) {
    if (key !== 'pagination' && key !== 'nextCursor') {
      meta[key] = value;
    }
  }
  return meta;
}

function extractErrors(parsed: unknown): { code: string; messages: string[] } {
  if (isPlainObject(parsed)) {
    const errors = parsed['errors'];
    const messages: string[] = [];
    if (Array.isArray(errors)) {
      for (const item of errors) {
        if (typeof item === 'string') {
          messages.push(item);
        }
      }
    }
    const meta = parsed['meta'];
    const code = isPlainObject(meta) ? str(meta['code'], 'UNKNOWN_ERROR') : 'UNKNOWN_ERROR';
    return { code, messages: messages.length > 0 ? messages : ['Неизвестная ошибка'] };
  }
  return { code: 'MALFORMED_RESPONSE', messages: ['Ответ backend не соответствует контракту'] };
}

/**
 * Единственная точка fetch. Возвращает { data, meta, headers } на успехе,
 * бросает ApiError на бизнес-ошибке/не-2xx. `data` — unknown: форму сужает маппер вызывающего.
 */
export async function request(path: string, options: RequestOptions = {}): Promise<ApiResponse> {
  return requestInternal(path, options, false, undefined);
}

async function requestInternal(
  path: string,
  options: RequestOptions,
  isRetry: boolean,
  fixedIdempotencyKey: string | undefined,
): Promise<ApiResponse> {
  if (!isRetry) {
    await maybeProactiveRefresh(path);
  }

  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...authHeaders(path) };
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let idempotencyKey = fixedIdempotencyKey;
  if (options.idempotent === true) {
    if (idempotencyKey === undefined) {
      idempotencyKey = crypto.randomUUID();
    }
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const url = buildUrl(path, options.searchParams);
  let response: Response;
  try {
    response = await fetch(url, { method, headers, body, signal: options.signal });
  } catch (error) {
    throw new ApiError(0, 'NETWORK_ERROR', [error instanceof Error ? error.message : 'Сетевая ошибка']);
  }

  // 401 на auth-роутах — бизнес-ответ (логин/refresh/logout), без interceptor.
  // На прочих: один refresh + retry; повторный 401 после retry — уже финал.
  if (response.status === 401 && !isAuthRoute(path)) {
    if (!isRetry) {
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) {
        return requestInternal(path, options, true, idempotencyKey);
      }
      // Если refresh окончательно провалился — handleUnauthorized уже вызван.
      // Если сеть/5xx — токены на месте, редиректа нет; кидаем 401 вызывающему.
      throw new ApiError(401, 'UNAUTHORIZED', ['Сессия истекла, войдите заново']);
    }
    handleUnauthorized();
    throw new ApiError(401, 'UNAUTHORIZED', ['Сессия истекла, войдите заново']);
  }

  let parsed: unknown = null;
  if (response.status !== 204) {
    try {
      parsed = await response.json();
    } catch {
      if (!response.ok) {
        throw new ApiError(response.status, 'MALFORMED_RESPONSE', [response.statusText || 'Ошибка запроса']);
      }
      throw new ApiError(response.status, 'MALFORMED_RESPONSE', ['Тело ответа не JSON']);
    }
  }

  // Разбор wire-оболочки. success:false — бизнес-ошибка (errors[]/meta.code).
  if (isPlainObject(parsed) && parsed['success'] === false) {
    const { code, messages } = extractErrors(parsed);
    throw new ApiError(response.status, code, messages);
  }

  if (!response.ok) {
    const { code, messages } = extractErrors(parsed);
    throw new ApiError(response.status, code, messages);
  }

  // Успех: { success:true, data, errors:null, meta } или укороченная { success, data }.
  if (isPlainObject(parsed) && parsed['success'] === true) {
    return { data: parsed['data'], meta: parseMeta(parsed['meta']), headers: response.headers };
  }

  // 204 или тело без оболочки — отдаём как есть (data=null для 204).
  return { data: parsed, meta: null, headers: response.headers };
}

/**
 * Бинарный/текстовый запрос (QR PNG, CSV-экспорт) — минуя JSON-разбор.
 * Возвращает Blob + заголовки (для X-Next-Cursor / имени файла). Bearer/tenant заголовки те же.
 * 401 → тот же single-flight refresh + retry 1 раз, что и у request.
 */
export async function requestBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; headers: Headers }> {
  return requestBlobInternal(path, options, false);
}

async function requestBlobInternal(
  path: string,
  options: RequestOptions,
  isRetry: boolean,
): Promise<{ blob: Blob; headers: Headers }> {
  if (!isRetry) {
    await maybeProactiveRefresh(path);
  }

  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...authHeaders(path) };
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  const url = buildUrl(path, options.searchParams);

  let response: Response;
  try {
    response = await fetch(url, { method, headers, body, signal: options.signal });
  } catch (error) {
    throw new ApiError(0, 'NETWORK_ERROR', [error instanceof Error ? error.message : 'Сетевая ошибка']);
  }

  if (response.status === 401 && !isAuthRoute(path)) {
    if (!isRetry) {
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) {
        return requestBlobInternal(path, options, true);
      }
      throw new ApiError(401, 'UNAUTHORIZED', ['Сессия истекла, войдите заново']);
    }
    handleUnauthorized();
    throw new ApiError(401, 'UNAUTHORIZED', ['Сессия истекла, войдите заново']);
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'REQUEST_FAILED', [response.statusText || 'Ошибка запроса']);
  }
  return { blob: await response.blob(), headers: response.headers };
}

export const apiClient = { request, requestBlob };
